import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import V2Button from '../button/v2_Button';
import V2Icon from '../icon/v2_Icon';
import './V2DropdownSelect.css';

const MENU_GAP = 4;
const VIEWPORT_MARGIN = 8;
const MENU_MIN_WIDTH = 180;

/**
 * Fixed-position coordinates for the portaled menu, computed from the trigger's viewport rect
 * so the menu can never be clipped by a scrollable/overflow-hidden ancestor (unlike
 * position:absolute, which is clipped by the nearest ancestor with overflow != visible).
 */
function computeMenuStyle({ triggerRect, menuHeight, viewportWidth, viewportHeight, align = 'left' }) {
  const availableBelow = viewportHeight - triggerRect.bottom - VIEWPORT_MARGIN;
  const availableAbove = triggerRect.top - VIEWPORT_MARGIN;
  const openUpwards = menuHeight > availableBelow && availableAbove > availableBelow;

  const top = openUpwards
    ? Math.max(VIEWPORT_MARGIN, triggerRect.top - menuHeight - MENU_GAP)
    : triggerRect.bottom + MENU_GAP;

  const width = Math.max(triggerRect.width, MENU_MIN_WIDTH);
  // 'right' anchors the menu's right edge to the trigger's right edge (grows leftward) —
  // for triggers that sit near a container's right edge.
  const naturalLeft = align === 'right' ? triggerRect.right - width : triggerRect.left;
  const left = Math.max(
    VIEWPORT_MARGIN,
    Math.min(naturalLeft, viewportWidth - width - VIEWPORT_MARGIN)
  );

  return { top, left, width };
}

function V2DropdownMenuItems({ options, value, itemClassName, activeItemClassName, onSelect }) {
  return options.map((option) => {
    const isSelected = value === option.value;
    // Additive per-option disable: the option stays visible in the menu but cannot be selected.
    const isDisabled = option.disabled === true;
    const itemClasses = [
      'v2-dropdown-select__item',
      itemClassName,
      isSelected ? 'v2-dropdown-select__item--active' : '',
      isSelected ? activeItemClassName : '',
      isDisabled ? 'v2-dropdown-select__item--disabled' : '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <button
        key={option.value}
        type="button"
        role="menuitemradio"
        aria-checked={isSelected}
        aria-disabled={isDisabled ? 'true' : undefined}
        disabled={isDisabled}
        className={itemClasses}
        onClick={isDisabled ? undefined : () => onSelect(option.value)}
      >
        <span className="v2-dropdown-select__item-content">
          <span className="v2-dropdown-select__item-text">
            <span className="v2-dropdown-select__item-label">{option.label}</span>
            {option.disabledReason && (
              <span className="v2-dropdown-select__item-reason">{option.disabledReason}</span>
            )}
          </span>
          {option.badge}
        </span>
        {isSelected && <V2Icon name="check" className="v2-icon--sm" />}
      </button>
    );
  });
}

/**
 * Reusable token-based dropdown control for simple single-select menus.
 *
 * @param {Object} props
 * @param {Array<{value: string, label: string, badge?: React.ReactNode, disabled?: boolean,
 *   disabledReason?: string}>} props.options
 *   `badge` (optional): extra node rendered after the label in the menu item only (e.g. a small
 *   V2Tag) — the trigger button's own label always stays plain text via `label`.
 *   `disabled` (optional): the item stays visible but cannot be selected; `disabledReason` is
 *   shown as a second line under the label.
 * @param {string} props.value
 * @param {(value: string) => void} props.onChange
 * @param {'status'|'plain'} [props.variant='plain']
 *   - `'status'`: trigger button switches to `activeButtonVariant` when a non-inactive value is
 *     selected, giving the user a visual signal that a filter/grouping is active.
 *   - `'plain'`: trigger button always stays on `buttonVariant` regardless of selection; no
 *     color change or active indicator — suitable for plain pickers where the selection has no
 *     persistent effect the user needs to be reminded of.
 * @param {string} [props.placeholder='Select']
 * @param {string} [props.selectedLabelPrefix='']
 * @param {Array<string>} [props.inactiveValues=['']]
 * @param {string} [props.buttonVariant='secondary']
 * @param {string} [props.activeButtonVariant='selected']
 * @param {boolean} [props.disabled=false]
 * @param {string} [props.className='']
 * @param {string} [props.buttonClassName='']
 * @param {string} [props.menuClassName='']
 * @param {string} [props.itemClassName='']
 * @param {string} [props.activeItemClassName='']
 * @param {boolean} [props.renderMenuInPortal=false]
 * @param {'left'|'right'} [props.menuAlign='left']
 *   Horizontal anchor when renderMenuInPortal is true: 'left' grows the menu rightward from the
 *   trigger's left edge (default); 'right' grows it leftward from the trigger's right edge —
 *   use for triggers that sit near a container's right edge.
 * @param {string} [props.icon='expand_more']
 *   Icon name on the trigger button — override to 'filter_alt' for filter-style pickers.
 * @param {boolean} [props.iconOnly=false]
 *   Hide the text label and render just the icon — the label is still exposed via aria-label.
 * @param {'leading'|'trailing'} [props.iconPosition='trailing']
 *   Where the icon sits relative to the label — 'trailing' (default) suits chevron-style
 *   pickers (e.g. 'expand_more'); use 'leading' for filter-style pickers (e.g. 'filter_alt').
 * @param {boolean} [props.openOnMount=false]
 *   Open the menu immediately on first render instead of requiring a trigger click — for
 *   drop-in replacements of inline table-cell editors that previously auto-focused/opened a
 *   native `<select>` (e.g. double-click-to-edit cells).
 * @param {(query: string) => void} [props.onSearchChange]
 *   Opt-in search box at the top of the menu (set it to enable the box). The parent filters `options` itself, so
 *   it can search on the server; `searchValue` is the box's value.
 * @param {string} [props.searchPlaceholder='Search...']
 * @param {boolean} [props.menuLoading=false] - Shows "Loading..." in the menu while a search runs.
 * @param {string} [props.emptyMessage='No results'] - Shown when `options` is empty.
 * @param {string} [props.ariaLabel]
 *   Explicit accessible name for the trigger button, overriding its default (the selected
 *   option's label, or the placeholder when nothing is selected) — use when the visible
 *   label alone doesn't convey the control's purpose (e.g. a bare "All"/"Filter" button).
 */
function V2DropdownSelect({
  options = [],
  value,
  onChange,
  variant = 'plain',
  placeholder = 'Select',
  selectedLabelPrefix = '',
  inactiveValues = [''],
  buttonVariant = 'secondary',
  activeButtonVariant = 'selected',
  disabled = false,
  className = '',
  buttonClassName = '',
  menuClassName = '',
  itemClassName = '',
  activeItemClassName = '',
  renderMenuInPortal = false,
  menuAlign = 'left',
  icon = 'expand_more',
  iconOnly = false,
  iconPosition = 'trailing',
  openOnMount = false,
  onSearchChange,
  searchValue = '',
  searchPlaceholder = 'Search...',
  menuLoading = false,
  emptyMessage = 'No results',
  ariaLabel,
}) {
  const [open, setOpen] = useState(() => openOnMount);
  const [menuStyle, setMenuStyle] = useState({});
  const rootRef = useRef(null);
  const menuRef = useRef(null);

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value),
    [options, value]
  );

  const isActiveSelection =
    Boolean(selectedOption) && !inactiveValues.includes(value);

  const buttonLabel = isActiveSelection
    ? `${selectedLabelPrefix}${selectedOption.label}`
    : placeholder;

  // In 'plain' variant the trigger always uses the base buttonVariant — no active colour signal.
  const resolvedButtonVariant =
    variant === 'plain' || !isActiveSelection ? buttonVariant : activeButtonVariant;

  const rootClasses = ['v2-dropdown-select', className].filter(Boolean).join(' ');
  const resolvedMenuClass = [
    'v2-dropdown-select__menu',
    renderMenuInPortal ? 'v2-dropdown-select__menu--portal' : '',
    menuClassName,
  ]
    .filter(Boolean)
    .join(' ');

  const handleSelect = (nextValue) => {
    setOpen(false);
    onChange?.(nextValue);
  };

  // Menu is portaled to document.body (see render below) so it escapes clipping by any
  // scrollable/overflow-hidden ancestor the trigger happens to sit inside.
  const updateMenuPosition = useCallback(() => {
    const triggerEl = rootRef.current;
    if (!triggerEl) return;

    setMenuStyle(computeMenuStyle({
      triggerRect: triggerEl.getBoundingClientRect(),
      menuHeight: menuRef.current?.offsetHeight || 0,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      align: menuAlign,
    }));
  }, [menuAlign]);

  // Runs after the menu commits to the DOM (but before paint) so its real height is available
  // for the up/down flip decision on the very first open — no positioning flicker. Also tracks
  // resize/scroll while open so a portaled menu (fixed to the viewport, not the trigger in the
  // DOM tree) stays anchored to it.
  useLayoutEffect(() => {
    if (!open) return undefined;
    updateMenuPosition();
    if (!renderMenuInPortal) return undefined;
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);
    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [open, renderMenuInPortal, updateMenuPosition]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleOutsideClick = (event) => {
      const clickedRoot = rootRef.current && rootRef.current.contains(event.target);
      const clickedMenu = menuRef.current && menuRef.current.contains(event.target);
      if (!clickedRoot && !clickedMenu) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [open]);


  const menu = open ? (
    <div
      ref={menuRef}
      className={resolvedMenuClass}
      role="menu"
      style={renderMenuInPortal ? menuStyle : undefined}
    >
      {onSearchChange && (
        <input
          type="search"
          className="v2-dropdown-select__search"
          aria-label={searchPlaceholder}
          placeholder={searchPlaceholder}
          value={searchValue}
          onChange={(event) => onSearchChange(event.target.value)}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
        />
      )}
      {onSearchChange && menuLoading && <p className="v2-dropdown-select__status">Loading...</p>}
      {onSearchChange && !menuLoading && options.length === 0 && (
        <p className="v2-dropdown-select__status">{emptyMessage}</p>
      )}
      <V2DropdownMenuItems
        options={options}
        value={value}
        itemClassName={itemClassName}
        activeItemClassName={activeItemClassName}
        onSelect={handleSelect}
      />
    </div>
  ) : null;

  return (
    <div ref={rootRef} className={rootClasses}>
      <V2Button
        type="button"
        variant={resolvedButtonVariant}
        onClick={() => setOpen((prev) => !prev)}
        className={buttonClassName}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={ariaLabel || (iconOnly ? buttonLabel : undefined)}
      >
        {iconPosition === 'leading' && <V2Icon name={icon} className="v2-icon--sm" />}
        {!iconOnly && buttonLabel}
        {iconPosition === 'trailing' && <V2Icon name={icon} className="v2-icon--sm" />}
      </V2Button>

      {renderMenuInPortal && menu ? createPortal(menu, document.body) : menu}
    </div>
  );
}

export default V2DropdownSelect;
